import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Skeleton,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@asm/ui";
import { ExternalLink, Building2, AlertCircle, Settings, ShieldAlert, ShieldCheck, Loader2, Webhook, Layers, Plus, Pencil, Check, X, Users, Mail, Trash2 } from "lucide-react";
import { adminApi, Organization, OrganizationSettings, OrgOverrides, Invitation, User } from "../lib/api";

const tierStyles: Record<string, string> = {
  enterprise: "bg-red-500/10 text-red-400 border-red-500/30",
  professional: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  starter: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  free: "bg-muted text-muted-foreground border-border",
};

export default function Organizations() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Settings dialog state
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [orgSettings, setOrgSettings] = useState<OrganizationSettings | null>(null);
  const [orgOverrides, setOrgOverrides] = useState<OrgOverrides | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Rename state
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");

  // Team tab state
  const [orgUsers, setOrgUsers] = useState<User[]>([]);
  const [orgInvitations, setOrgInvitations] = useState<Invitation[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createSlug, setCreateSlug] = useState("");
  const [createTier, setCreateTier] = useState("free");
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await adminApi.listOrganizations();
        setOrganizations(response.organizations);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load organizations");
      } finally {
        setLoading(false);
      }
    };
    fetchOrganizations();
  }, []);

  // Load settings when org is selected
  const openSettings = async (org: Organization) => {
    setSelectedOrg(org);
    setSettingsLoading(true);
    setSettingsError(null);
    setOrgSettings(null);
    setOrgOverrides(null);
    setEditingName(false);

    try {
      const [settings, overrides] = await Promise.all([
        adminApi.getOrganizationSettings(org.id),
        adminApi.getOrgOverrides(org.id),
      ]);
      setOrgSettings(settings);
      setOrgOverrides(overrides);
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : "Failed to load settings");
    } finally {
      setSettingsLoading(false);
    }
  };

  const closeSettings = () => {
    setSelectedOrg(null);
    setOrgSettings(null);
    setOrgOverrides(null);
    setSettingsError(null);
    setEditingName(false);
    setOrgUsers([]);
    setOrgInvitations([]);
    setTeamError(null);
    setInviteEmail("");
    setInviteRole("member");
  };

  // Load team data for an org
  const loadTeam = async (orgId: string) => {
    setTeamLoading(true);
    setTeamError(null);
    try {
      const [usersRes, invRes] = await Promise.all([
        adminApi.listUsers({ org_id: orgId, per_page: 100 }),
        adminApi.listOrgInvitations(orgId),
      ]);
      setOrgUsers(usersRes.users);
      setOrgInvitations(invRes.data);
    } catch (err) {
      setTeamError(err instanceof Error ? err.message : "Failed to load team");
    } finally {
      setTeamLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!selectedOrg || !inviteEmail.trim()) return;
    setInviting(true);
    setTeamError(null);
    try {
      const res = await adminApi.inviteToOrg(selectedOrg.id, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setOrgInvitations((prev) => [...prev, res.data]);
      setInviteEmail("");
      setInviteRole("member");
    } catch (err) {
      setTeamError(err instanceof Error ? err.message : "Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!selectedOrg) return;
    try {
      await adminApi.cancelOrgInvitation(selectedOrg.id, invitationId);
      setOrgInvitations((prev) => prev.filter((i) => i.id !== invitationId));
    } catch (err) {
      setTeamError(err instanceof Error ? err.message : "Failed to cancel invitation");
    }
  };

  // Rename organization
  const startRename = () => {
    if (!selectedOrg) return;
    setNameInput(selectedOrg.name);
    setEditingName(true);
  };

  const cancelRename = () => {
    setEditingName(false);
    setNameInput("");
  };

  const saveRename = async () => {
    if (!selectedOrg || !nameInput.trim()) return;
    if (nameInput.trim() === selectedOrg.name) {
      setEditingName(false);
      return;
    }

    setSaving(true);
    try {
      const updated = await adminApi.updateOrganization(selectedOrg.id, { name: nameInput.trim() });
      setSelectedOrg(updated);
      setOrganizations((prev) =>
        prev.map((org) => (org.id === updated.id ? updated : org))
      );
      setEditingName(false);
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : "Failed to rename organization");
    } finally {
      setSaving(false);
    }
  };

  // Toggle skip_domain_verification
  const toggleDomainVerificationBypass = async () => {
    if (!selectedOrg || !orgSettings) return;

    setSaving(true);
    try {
      const newValue = !orgSettings.skip_domain_verification;
      const updated = await adminApi.updateOrganizationSettings(selectedOrg.id, {
        skip_domain_verification: newValue,
      });
      setOrgSettings(updated);
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : "Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  // Toggle webhook override for an org
  const toggleWebhookOverride = async () => {
    if (!selectedOrg) return;

    setSaving(true);
    try {
      const currentlyEnabled = orgOverrides?.webhooks_enabled;
      const newValue = currentlyEnabled ? undefined : true;
      const updated = await adminApi.setOrgOverrides(selectedOrg.id, {
        webhooks_enabled: newValue,
        max_webhooks: newValue ? 3 : undefined,
      });
      setOrgOverrides(updated);
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : "Failed to update webhook override");
    } finally {
      setSaving(false);
    }
  };

  // Change organization tier
  const changeTier = async (newTier: string) => {
    if (!selectedOrg || newTier === selectedOrg.tier) return;

    setSaving(true);
    try {
      const updated = await adminApi.updateOrganizationTier(selectedOrg.id, newTier);
      setSelectedOrg(updated);
      setOrganizations((prev) =>
        prev.map((org) => (org.id === updated.id ? updated : org))
      );
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : "Failed to update tier");
    } finally {
      setSaving(false);
    }
  };

  // Create organization
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createName.trim()) return;

    setCreateLoading(true);
    setCreateError(null);
    try {
      const org = await adminApi.createOrganization({
        name: createName.trim(),
        slug: createSlug.trim() || undefined,
        tier: createTier,
      });
      setOrganizations((prev) => [org, ...prev]);
      setCreateOpen(false);
      setCreateName("");
      setCreateSlug("");
      setCreateTier("free");
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create organization");
    } finally {
      setCreateLoading(false);
    }
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreateName("");
    setCreateSlug("");
    setCreateTier("free");
    setCreateError(null);
  };

  // Auto-generate slug hint from name
  const slugHint = createName
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

  return (
    <div className="tactical-grid space-y-8">
      {/* Header */}
      <div className="animate-fade-in-up">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <Building2 className="h-8 w-8 text-red-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Organizations</h1>
              <p className="text-muted-foreground">Manage all organizations on the platform</p>
            </div>
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-red-500 hover:bg-red-400 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Organization
          </Button>
        </div>
      </div>

      {error && (
        <div className="animate-fade-in-up rounded-lg border border-red-500/30 bg-red-500/5 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {/* Organizations Table */}
      <Card className="animate-fade-in-up stagger-1 border-border bg-card/50">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-6 w-1 rounded-full bg-red-500" />
            <div>
              <CardTitle>All Organizations</CardTitle>
              <CardDescription>View and manage organization accounts</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : organizations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="p-3 rounded-lg bg-muted/30 border border-border mb-4">
                <Building2 className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground text-sm">No organizations found</p>
            </div>
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <TableHead className="font-semibold">Name</TableHead>
                    <TableHead className="font-semibold">Slug</TableHead>
                    <TableHead className="font-semibold">Tier</TableHead>
                    <TableHead className="font-semibold">Created</TableHead>
                    <TableHead className="w-[100px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {organizations.map((org, index) => (
                    <TableRow
                      key={org.id}
                      className={`hover:bg-muted/50 transition-colors animate-fade-in-up stagger-${index + 1}`}
                    >
                      <TableCell className="font-medium">{org.name}</TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">
                        {org.slug}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={tierStyles[org.tier] || tierStyles.free}
                        >
                          {org.tier}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(org.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 hover:bg-amber-500/10 hover:text-amber-400"
                            onClick={() => openSettings(org)}
                            title="Organization Settings"
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 hover:bg-red-500/10 hover:text-red-400"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Organization Dialog */}
      <Dialog open={createOpen} onOpenChange={(open) => !open && closeCreate()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Create Organization
            </DialogTitle>
            <DialogDescription>
              Add a new organization to the platform
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="py-4 space-y-4">
              {createError && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
                  <span className="text-red-400 text-sm">{createError}</span>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="orgName">Name</Label>
                <Input
                  id="orgName"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="Acme Corporation"
                  required
                  autoFocus
                  className="bg-muted/50 border-border"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="orgSlug">Slug (optional)</Label>
                <Input
                  id="orgSlug"
                  value={createSlug}
                  onChange={(e) => setCreateSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  placeholder={slugHint || "auto-generated-from-name"}
                  className="bg-muted/50 border-border font-mono text-sm"
                />
                {createName && !createSlug && (
                  <p className="text-xs text-muted-foreground">
                    Will use: <span className="font-mono">{slugHint}</span>
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Tier</Label>
                <Select value={createTier} onValueChange={setCreateTier}>
                  <SelectTrigger className="bg-muted/50 border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(tierStyles).map(([tier]) => (
                      <SelectItem key={tier} value={tier}>
                        <span className="capitalize">{tier}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeCreate}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createLoading || !createName.trim()}
                className="bg-red-500 hover:bg-red-400 text-white"
              >
                {createLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  "Create"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={!!selectedOrg} onOpenChange={(open) => !open && closeSettings()}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Organization Settings
            </DialogTitle>
            <DialogDescription>
              {selectedOrg?.slug}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="settings" onValueChange={(val) => {
            if (val === "team" && selectedOrg && orgUsers.length === 0 && !teamLoading) {
              loadTeam(selectedOrg.id);
            }
          }}>
            <TabsList className="bg-muted/50 p-1 mb-4">
              <TabsTrigger value="settings" className="data-[state=active]:bg-background gap-2">
                <Settings className="h-3.5 w-3.5" />
                Settings
              </TabsTrigger>
              <TabsTrigger value="team" className="data-[state=active]:bg-background gap-2">
                <Users className="h-3.5 w-3.5" />
                Team
              </TabsTrigger>
            </TabsList>

            <TabsContent value="settings">
              <div className="space-y-6">
                {settingsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : settingsError ? (
                  <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4 flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <span className="text-red-400 text-sm">{settingsError}</span>
                  </div>
                ) : orgSettings ? (
                  <>
                    {/* Organization Name */}
                    <div className="rounded-lg border border-border p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-muted/30 border border-border">
                          <Building2 className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm">Organization Name</h4>
                          {editingName ? (
                            <div className="flex items-center gap-2 mt-2">
                              <Input
                                value={nameInput}
                                onChange={(e) => setNameInput(e.target.value)}
                                className="bg-muted/50 border-border h-9"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") { e.preventDefault(); saveRename(); }
                                  if (e.key === "Escape") cancelRename();
                                }}
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 shrink-0 hover:bg-emerald-500/10 hover:text-emerald-400"
                                onClick={saveRename}
                                disabled={saving || !nameInput.trim()}
                              >
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 shrink-0 hover:bg-red-500/10 hover:text-red-400"
                                onClick={cancelRename}
                                disabled={saving}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-sm">{selectedOrg?.name}</span>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 hover:bg-amber-500/10 hover:text-amber-400"
                                onClick={startRename}
                                disabled={saving}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Tier Selector */}
                    <div className="rounded-lg border border-border p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${tierStyles[selectedOrg?.tier ?? "free"]} border`}>
                          <Layers className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm">Organization Tier</h4>
                          <p className="text-xs text-muted-foreground mt-1">
                            Controls rate limits, concurrent scans, domain quotas, and feature access.
                          </p>
                          <Select
                            value={selectedOrg?.tier}
                            onValueChange={changeTier}
                            disabled={saving}
                          >
                            <SelectTrigger className="mt-3 w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(tierStyles).map(([tier]) => (
                                <SelectItem key={tier} value={tier}>
                                  <span className="capitalize">{tier}</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Domain Verification Bypass Setting */}
                    <div className="rounded-lg border border-border p-4 space-y-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          {orgSettings.skip_domain_verification ? (
                            <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
                              <ShieldAlert className="h-5 w-5 text-amber-400" />
                            </div>
                          ) : (
                            <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                              <ShieldCheck className="h-5 w-5 text-emerald-400" />
                            </div>
                          )}
                          <div>
                            <h4 className="font-semibold text-sm">Skip Domain Verification</h4>
                            <p className="text-xs text-muted-foreground mt-1">
                              Allow active scanning modules (portscan, subdomain bruteforce, cloud misconfig) without DNS TXT verification.
                            </p>
                          </div>
                        </div>
                        <Button
                          variant={orgSettings.skip_domain_verification ? "destructive" : "outline"}
                          size="sm"
                          onClick={toggleDomainVerificationBypass}
                          disabled={saving}
                          className="shrink-0"
                        >
                          {saving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : orgSettings.skip_domain_verification ? (
                            "Disable"
                          ) : (
                            "Enable"
                          )}
                        </Button>
                      </div>

                      {orgSettings.skip_domain_verification && (
                        <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                            <div className="text-xs text-amber-400">
                              <strong>Security Warning:</strong> This organization can perform active scanning on any domain without proving ownership. Ensure a signed Statement of Work (SOW) is on file for authorized penetration testing engagements.
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Webhook Override */}
                    <div className="rounded-lg border border-border p-4 space-y-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${orgOverrides?.webhooks_enabled ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-muted/30 border border-border"}`}>
                            <Webhook className={`h-5 w-5 ${orgOverrides?.webhooks_enabled ? "text-emerald-400" : "text-muted-foreground"}`} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-sm">Webhook Override</h4>
                            <p className="text-xs text-muted-foreground mt-1">
                              Grant webhook access regardless of tier. Useful for Free tier orgs that need webhook notifications.
                            </p>
                          </div>
                        </div>
                        <Button
                          variant={orgOverrides?.webhooks_enabled ? "destructive" : "outline"}
                          size="sm"
                          onClick={toggleWebhookOverride}
                          disabled={saving}
                          className="shrink-0"
                        >
                          {saving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : orgOverrides?.webhooks_enabled ? (
                            "Remove"
                          ) : (
                            "Enable"
                          )}
                        </Button>
                      </div>

                      {orgOverrides?.webhooks_enabled && (
                        <div className="text-xs text-muted-foreground">
                          Override active: webhooks enabled with max {orgOverrides.max_webhooks ?? "unlimited"} webhooks.
                        </div>
                      )}
                    </div>
                  </>
                ) : null}
              </div>
            </TabsContent>

            <TabsContent value="team">
              <div className="space-y-6">
                {teamError && (
                  <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
                    <span className="text-red-400 text-sm">{teamError}</span>
                  </div>
                )}

                {/* Invite Form */}
                <div className="rounded-lg border border-border p-4 space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    Invite User
                  </h4>
                  <div className="flex items-end gap-2">
                    <div className="flex-1 space-y-1">
                      <Label htmlFor="inviteEmail" className="text-xs text-muted-foreground">Email</Label>
                      <Input
                        id="inviteEmail"
                        type="email"
                        placeholder="user@example.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        className="bg-muted/50 border-border h-9"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); handleInvite(); }
                        }}
                      />
                    </div>
                    <div className="w-28 space-y-1">
                      <Label className="text-xs text-muted-foreground">Role</Label>
                      <Select value={inviteRole} onValueChange={setInviteRole}>
                        <SelectTrigger className="h-9 bg-muted/50 border-border">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="viewer">Viewer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      size="sm"
                      className="bg-red-500 hover:bg-red-400 text-white h-9"
                      onClick={handleInvite}
                      disabled={inviting || !inviteEmail.trim()}
                    >
                      {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Invite"}
                    </Button>
                  </div>
                </div>

                {/* Members */}
                <div className="rounded-lg border border-border p-4 space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    Members ({orgUsers.length})
                  </h4>
                  {teamLoading ? (
                    <div className="space-y-2">
                      {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full rounded" />)}
                    </div>
                  ) : orgUsers.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">No members yet</p>
                  ) : (
                    <div className="rounded-lg border border-border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30 hover:bg-muted/30">
                            <TableHead className="font-semibold text-xs">Email</TableHead>
                            <TableHead className="font-semibold text-xs">Name</TableHead>
                            <TableHead className="font-semibold text-xs">Role</TableHead>
                            <TableHead className="font-semibold text-xs">Joined</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {orgUsers.map((u) => (
                            <TableRow key={u.id} className="hover:bg-muted/50">
                              <TableCell className="text-sm font-mono">{u.email}</TableCell>
                              <TableCell className="text-sm">{u.name || "—"}</TableCell>
                              <TableCell>
                                <Badge
                                  variant="outline"
                                  className={
                                    u.role === "admin"
                                      ? "bg-red-500/10 text-red-400 border-red-500/30"
                                      : u.role === "viewer"
                                      ? "bg-muted text-muted-foreground"
                                      : "bg-cyan-500/10 text-cyan-400 border-cyan-500/30"
                                  }
                                >
                                  {u.role}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {new Date(u.created_at).toLocaleDateString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>

                {/* Pending Invitations */}
                <div className="rounded-lg border border-border p-4 space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    Pending Invitations ({orgInvitations.length})
                  </h4>
                  {teamLoading ? (
                    <div className="space-y-2">
                      {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-10 w-full rounded" />)}
                    </div>
                  ) : orgInvitations.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">No pending invitations</p>
                  ) : (
                    <div className="rounded-lg border border-border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30 hover:bg-muted/30">
                            <TableHead className="font-semibold text-xs">Email</TableHead>
                            <TableHead className="font-semibold text-xs">Role</TableHead>
                            <TableHead className="font-semibold text-xs">Expires</TableHead>
                            <TableHead className="w-[60px]" />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {orgInvitations.map((inv) => (
                            <TableRow key={inv.id} className="hover:bg-muted/50">
                              <TableCell className="text-sm font-mono">{inv.email}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="capitalize">{inv.role}</Badge>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {new Date(inv.expires_at).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 hover:bg-red-500/10 hover:text-red-400"
                                  onClick={() => handleCancelInvitation(inv.id)}
                                  title="Cancel invitation"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={closeSettings}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
